// src/app/login/page.tsx
"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Lock,
  Mail,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
  ShieldCheck,
  KeyRound,
  CheckCircle2,
  Eye,
  EyeOff,
  Sparkles,
} from "lucide-react";

export default function MerchantLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // 🌟 2FA State Management
  const [is2FAStep, setIs2FAStep] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const rootDomain = process.env.NEXT_PUBLIC_ROOT_DOMAIN || "shop.xylotechsolution.com";

  const getApiBase = () => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return "http://localhost:8000/api/v1/tenants";
      }
      return "/api/v1/tenants";
    }
    return "/api/v1/tenants";
  };

  const getRedirectUrl = (subdomain: string, token: string) => {
    if (typeof window === "undefined") return "/";
    const host = window.location.hostname;
    const port = window.location.port;
    const protocol = window.location.protocol;
    const isLocal = host.includes("localhost") || host === "127.0.0.1";
    const targetSub = subdomain && subdomain !== "app" ? subdomain : "nextlook";

    if (isLocal) {
      return `${protocol}//${targetSub}.localhost${port ? `:${port}` : ""}/admin?sso_token=${token}`;
    }
    return `${protocol}//${targetSub}.${rootDomain}/admin?sso_token=${token}`;
  };

  // Step 1: Handle Email & Password Login
  const handleMerchantLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);

    try {
      const res = await fetch(`${getApiBase()}/merchant-login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });

      const data = await res.json();

      if (res.ok) {
        // 🌟 যদি 2FA সক্রিয় থাকে, তাহলে OTP ভেরিফিকেশন স্টেপে যাবে
        if (data.requires_2fa) {
          setTempToken(data.temp_token);
          setIs2FAStep(true);
          return;
        }

        // 🌟 2FA না থাকলে সরাসরি SSO টোকেনসহ অ্যাডমিন ড্যাশবোর্ডে রিডাইরেক্ট
        const token = data.access || data.token;
        if (token) {
          localStorage.setItem("merchant_token", token);
          localStorage.setItem("token", token);
          const targetSubdomain = data.subdomain || data.store?.subdomain || "nextlook";
          window.location.href = getRedirectUrl(targetSubdomain, token);
        }
      } else {
        setErrorMsg(data.error || data.detail || "ইমেইল অথবা পাসওয়ার্ড সঠিক নয়। পুনরায় চেষ্টা করুন।");
      }
    } catch {
      setErrorMsg("সার্ভারের সাথে সংযোগ স্থাপন করা যায়নি।");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Handle 2FA OTP Submission
  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode.trim()) {
      setErrorMsg("দয়া করে ৬ ডিজিটের OTP কোডটি লিখুন।");
      return;
    }

    setErrorMsg("");
    setLoading(true);

    try {
      const res = await fetch(`${getApiBase()}/verify-2fa/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          temp_token: tempToken,
          otp: otpCode.trim(),
        }),
      });

      const data = await res.json();

      if (res.ok) {
        const token = data.access || data.token;
        if (token) {
          localStorage.setItem("merchant_token", token);
          localStorage.setItem("token", token);
          const targetSubdomain = data.subdomain || data.store?.subdomain || "nextlook";
          window.location.href = getRedirectUrl(targetSubdomain, token);
        }
      } else {
        setErrorMsg(data.error || "OTP কোডটি সঠিক নয়। পুনরায় চেষ্টা করুন।");
      }
    } catch {
      setErrorMsg("সার্ভার ত্রুটি। অনুগ্রহ করে আবার চেষ্টা করুন।");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-100 flex flex-col justify-between font-sans selection:bg-amber-500/30 selection:text-amber-200">
      
      {/* 🌟 ১. টপ নেভবার (মূল Xylo Tech Solutions ব্র্যান্ডিং) */}
      <header className="border-b border-white/5 px-6 sm:px-12 py-3.5 sticky top-0 bg-[#07090E]/90 backdrop-blur-xl z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link href="https://xylotechsolution.com" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-black/60 border border-white/10 group-hover:border-amber-500/50 flex items-center justify-center p-2 transition-all shadow-lg shadow-black/60">
              <Image
                src="/logo.png"
                alt="Xylo Tech Solutions"
                width={32}
                height={32}
                className="w-full h-full object-contain"
                priority
              />
            </div>
            <div className="flex flex-col">
              <span className="font-black text-sm tracking-wider text-white uppercase group-hover:text-amber-400 transition-colors">
                Xylo Merchant Portal
              </span>
              <span className="text-[10px] text-amber-500/90 font-mono tracking-widest font-semibold uppercase">
                SaaS & E-Commerce Ecosystem
              </span>
            </div>
          </Link>

          <Link
            href="/"
            className="text-xs font-bold text-slate-300 hover:text-amber-400 flex items-center gap-1.5 transition-colors group"
          >
            <span>নতুন দোকান খুলুন</span>
            <ArrowRight className="w-3.5 h-3.5 text-amber-400 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </header>

      {/* 🌟 ২. সেন্ট্রাল সাইন-ইন কার্ড */}
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
        <div className="max-w-md w-full bg-[#0E121C] border border-white/10 rounded-3xl p-7 sm:p-9 space-y-6 shadow-2xl relative overflow-hidden">
          
          {/* ব্যাকগ্রাউন্ড গোল্ডেন গ্লো এফেক্ট */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-amber-600/10 rounded-full blur-3xl pointer-events-none" />

          {/* আইকন ও শিরোনাম */}
          <div className="text-center space-y-2 relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mx-auto shadow-inner text-amber-400 mb-3">
              {is2FAStep ? (
                <KeyRound className="w-7 h-7 animate-pulse" />
              ) : (
                <ShieldCheck className="w-7 h-7" />
              )}
            </div>

            <h1 className="text-2xl font-black text-white tracking-tight">
              {is2FAStep ? "Two-Factor Verification" : "Merchant Sign-In"}
            </h1>
            <p className="text-xs text-slate-400 leading-relaxed">
              {is2FAStep
                ? "আপনার অথেনটিকেটর অ্যাপ অথবা ফোনের ৬-ডিজিট OTP কোডটি লিখুন"
                : "আপনার অনলাইন স্টোর ম্যানেজ করতে ক্রেডেনশিয়াল দিয়ে লগইন করুন"}
            </p>
          </div>

          {/* এরর মেসেজ */}
          {errorMsg && (
            <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-medium flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* ধাপ ১: ইমেইল ও পাসওয়ার্ড ফর্ম */}
          {!is2FAStep ? (
            <form onSubmit={handleMerchantLogin} className="space-y-4 text-xs relative z-10">
              <div className="space-y-1.5">
                <label className="font-bold text-slate-300 block">Merchant Email</label>
                <div className="relative flex items-center">
                  <Mail className="w-4 h-4 absolute left-3.5 text-slate-500" />
                  <input
                    type="email"
                    required
                    placeholder="merchant@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-slate-300 block">Password</label>
                <div className="relative flex items-center">
                  <Lock className="w-4 h-4 absolute left-3.5 text-slate-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-black/50 border border-white/10 text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 text-slate-500 hover:text-slate-300 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* লগইন বোতাম */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-black text-xs transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-black" />
                    <span>যাচাই করা হচ্ছে...</span>
                  </>
                ) : (
                  <>
                    <span>Dashboard-এ প্রবেশ করুন</span>
                    <ArrowRight className="w-3.5 h-3.5 text-black" />
                  </>
                )}
              </button>
            </form>
          ) : (
            /* ধাপ ২: 2FA OTP সাবমিশন ফর্ম */
            <form onSubmit={handleVerify2FA} className="space-y-5 text-xs relative z-10">
              <div className="space-y-2 text-center">
                <label className="font-bold text-slate-300 block text-xs">Security Verification Code (OTP)</label>
                <input
                  type="text"
                  maxLength={6}
                  required
                  autoFocus
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full py-3.5 text-center text-3xl tracking-[0.45em] font-mono font-black rounded-2xl bg-black/50 border border-white/10 text-amber-400 focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={loading || otpCode.length < 6}
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-black text-xs transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-black" />
                    <span>যাচাই হচ্ছে...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-black" />
                    <span>Verify & Login</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIs2FAStep(false);
                  setOtpCode("");
                  setErrorMsg("");
                }}
                className="w-full py-2 text-slate-400 hover:text-white flex items-center justify-center gap-1.5 transition-colors cursor-pointer text-xs"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>অন্য অ্যাকাউন্ট দিয়ে লগইন করুন</span>
              </button>
            </form>
          )}

          {/* সাবটেক্সট */}
          <div className="pt-2 text-center text-[11px] text-slate-500 border-t border-white/5 space-y-1">
            <p>আপনি কি সাধারণ ক্রেতা? শপিং করতে সরাসরি নির্দিষ্ট স্টোরের ঠিকানায় যান।</p>
          </div>
        </div>
      </main>

      {/* 🌟 ৩. ফুটার */}
      <footer className="border-t border-white/5 py-5 px-6 text-center text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-between max-w-7xl mx-auto w-full gap-3">
        <div className="flex items-center gap-2">
          <Image src="/logo.png" alt="Xylo Logo" width={16} height={16} className="object-contain opacity-80" />
          <span>© 2026 Xylo Tech Solutions. All rights reserved.</span>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <a
            href="https://xylotechsolution.com"
            target="_blank"
            rel="noreferrer"
            className="hover:text-amber-400 transition-colors"
          >
            Official Website
          </a>
          <span>•</span>
          <span className="text-slate-600">Enterprise Multi-Tenant SaaS Portal</span>
        </div>
      </footer>
    </div>
  );
}